/**
 * The monitoring story: one honest sentence-pair derived from the scan facts,
 * shared by the dashboard tile and the reports screen.
 *
 * The backend keeps three scan states apart because collapsing any two is a
 * false reassurance in a safety product (src/reports/service.ts): 'scanning'
 * (a completed scan exists), 'pending' (queued or never run — an empty report
 * means UNTESTED, not clear), 'unavailable' (discovery refused — set up, but
 * nobody is looking). This module renders each state with tension but never
 * past the facts: no "you're safe", no cadence promises (cadence comes from
 * next_scan_after, which is adaptive), and never "scanned" before a scan
 * completed.
 */

export interface MonitoringFacts {
  scanState: string;
  lastRunAt: string | null;
  firstScanCompletedAt: string | null;
  monitoredSources: number | null;
  nextScanAfter: string | null;
  unavailableReason: string | null;
  activeHits: number;
  liveExposureCount: number;
  /** All hits ever, resolved included. The home read does not carry it —
   *  undefined means unknown, and the copy must not claim "never found". */
  totalHits?: number;
}

export interface MonitoringStory {
  tone: 'alert' | 'active' | 'waiting' | 'limited';
  headline: string;
  detail: string;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function monitoringStory(facts: MonitoringFacts): MonitoringStory {
  // Hits outrank everything: found exposure is the one state where the next
  // move is the user's, whatever the scanner is doing meanwhile.
  if (facts.activeHits > 0) {
    const hits = `${String(facts.activeHits)} active hit${facts.activeHits === 1 ? '' : 's'}`;
    const live =
      facts.liveExposureCount > 0
        ? ` ${String(facts.liveExposureCount)} ${facts.liveExposureCount === 1 ? 'is' : 'are'} still live where we found ${facts.liveExposureCount === 1 ? 'it' : 'them'}.`
        : '';
    return {
      tone: 'alert',
      headline: `${hits} need${facts.activeHits === 1 ? 's' : ''} your review`,
      detail: `We found your photos being used.${live} Review each one and tell us what it is — that is what moves a takedown forward.`,
    };
  }

  if (facts.scanState === 'unavailable') {
    return {
      tone: 'limited',
      headline: 'Monitoring is set up — scanning is paused',
      detail: `${facts.unavailableReason ?? 'Discovery is not available for this account yet.'} Your photos stay registered, and scanning starts on its own the moment it becomes available.`,
    };
  }

  if (facts.scanState === 'scanning') {
    const parts: string[] = [];
    if (facts.lastRunAt !== null) parts.push(`Last scan finished ${shortDate(facts.lastRunAt)}.`);
    if (facts.monitoredSources !== null && facts.monitoredSources > 0) {
      parts.push(
        `Watching ${String(facts.monitoredSources)} source${facts.monitoredSources === 1 ? '' : 's'} for reposts of your photos.`,
      );
    }
    // "Never found" is only claimable when the total says so — active 0 with
    // resolved hits behind it is a different (and better-earned) sentence.
    parts.push(
      facts.totalHits === 0
        ? 'No reposts of your uploaded photos have been found so far.'
        : 'No matches need your attention right now.',
    );
    if (facts.nextScanAfter !== null) {
      parts.push(`Your next scan runs after ${shortDate(facts.nextScanAfter)}.`);
    }
    return { tone: 'active', headline: 'Monitoring is live', detail: parts.join(' ') };
  }

  // 'pending', with a completed scan behind it: a fresh pass is in flight.
  if (facts.firstScanCompletedAt !== null) {
    return {
      tone: 'waiting',
      headline: 'A scan is in progress',
      detail: `Your photos are being checked against the monitored sources right now${facts.lastRunAt === null ? '' : ` — the last pass finished ${shortDate(facts.lastRunAt)}`}. Anything it finds lands here the moment it does.`,
    };
  }

  // 'pending' and never scanned. An empty report here means UNTESTED — the
  // copy must carry momentum without claiming a scan that has not happened.
  return {
    tone: 'waiting',
    headline: 'Your photos are in — first scan is queued',
    detail:
      'We are lining up your first pass across the monitored sources. It starts on its own, monitoring begins the moment it completes, and anything found shows up here immediately.',
  };
}
